import { ProposalDAOInterface } from '../../ProposalDAO';
import { ComparisonItemBasedProposalDAO } from '../../ProposalDAOImpl';
import { ComparisonItemDAO } from '../../ProposalItemDAO';
import * as ProposalNS from '../../../Proposal';
import * as ProposalElementNS from '../../../ProposalElements';

// Utils
import { formatLocalizedString } from '../../../../../utils/i18n';
import { ROLLING_INTERVALS } from '../../../../../utils/portfolios';
import { deepClone } from '../../../../../utils/objects';
import { ProposalFieldDAO } from '../../ProposalFieldDAO';
import { ProposalStyleClassNames } from '../../../ProposalStyleClassNames';
import { ProposalItemLabelCell } from '../../../../../views/components/cells';
import { PROPOSAL_ORIENTATION } from '../../../utils/constants';
import { validIncomeStreamItem } from '../../../../../utils/income-stream';
import { MAX_DISTRIBUTION_SCENARIOS_PORTRAIT, MAX_DISTRIBUTION_SCENARIOS_LANDSCAPE } from '../../../utils/income-stream';
import { currencyToText } from '@/utils/currency';
import LineSeriesDataPoint from '@/types/line-series-data-point';
import ChartItem from '@/types/chart-item';
import ProposalToolbarOption from '@/types/proposal-toolbar-option';
import LineSeriesData from '@/types/line-series-data';
import RollingIntervals from '@/types/rolling-intervals';
import IncomeStreamScenario from '@/types/income-stream-scenario';
import ComparisonItem from '@/types/comparison-item';

export default class IncomeStreamGraphDAO extends ComparisonItemBasedProposalDAO {
  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new IncomeStreamGraphDAO(proposal, proposalElement);
  }

  series: LineSeriesDataPoint[][];

  formattedChartItems: ChartItem[];

  toolbarOption: ProposalToolbarOption | undefined;

  interval: RollingIntervals;

  colorOverride: string | null = null;

  out: LineSeriesData[] = [];

  constructor(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement) {
    super(proposal, proposalElement);
    this.series = [];
    this.formattedChartItems = [];
    this.toolbarOption = {};
    this.interval = ROLLING_INTERVALS.WEEKLY;
  }

  /* eslint-disable-next-line class-methods-use-this */
  protected getColorKeys(): string[] {
    return [];
  }

  /* eslint-disable-next-line class-methods-use-this */
  protected build(): void {
    const itemDAOs: ComparisonItemDAO[] = this.getItemDAOs();

    this.out = itemDAOs.reduce((acc, itemDAO) => {
      const { item } = itemDAO;

      if (validIncomeStreamItem(itemDAO.item) && item.scenarios !== undefined) {
        this.addItemField(itemDAO);

        let defaultScenarioData: LineSeriesDataPoint[] = [];
        if (item.scenarios.defaultIncomeStream !== undefined && item.scenarios.defaultIncomeStream.data) {
          defaultScenarioData = deepClone(item.scenarios.defaultIncomeStream.data.graphData);
        }
        const otherScenarios: IncomeStreamScenario[] = deepClone(item.scenarios.incomeStreamDistribution) || [];

        if (!defaultScenarioData) {
          return acc;
        }

        // Merge the default scenario with the dynamic ones if there are any

        const limit: number =
          this.proposal.options.orientation === PROPOSAL_ORIENTATION.PORTRAIT ? MAX_DISTRIBUTION_SCENARIOS_PORTRAIT : MAX_DISTRIBUTION_SCENARIOS_LANDSCAPE;

        const othersRaw: (LineSeriesDataPoint[] | null | undefined)[] = otherScenarios.map(s => s.data && s.data.graphData).slice(0, limit);
        const others: LineSeriesDataPoint[][] = [];
        for (const raw of othersRaw) {
          if (raw != null) {
            others.push(raw);
          }
        }

        const itemData: LineSeriesDataPoint[][] = [defaultScenarioData, ...others];

        return acc.concat(...itemData.map((data: LineSeriesDataPoint[], i: number) => this.formatSeries(data, item, i)));
      }

      return acc;
    }, [] as LineSeriesData[]);
  }

  getLineSeries(): LineSeriesData[] {
    return this.out;
  }

  getItemFields(): ProposalFieldDAO[] {
    return this.getItemDAOs().reduce((acc, itemDAO) => {
      if (validIncomeStreamItem(itemDAO.item)) {
        const itemfield = itemDAO.getFieldDAO(0);

        if (itemfield) {
          acc.push(itemDAO.getFieldDAO(0));
        }
      }
      return acc;
    }, [] as ProposalFieldDAO[]);
  }

  // private methods

  private addItemField(itemDAO: ComparisonItemDAO): void {
    const name = formatLocalizedString(itemDAO.item.name, this.locale);
    const field: ProposalFieldDAO = new ProposalFieldDAO('', name, ProposalStyleClassNames.ProposalTableContent);
    field.cellType = ProposalItemLabelCell;
    field.color = itemDAO.item.color;
    field.itemType = itemDAO.item.type;
    field.currency = this.proposal.showBaseCurrencies ? itemDAO.item.currency : undefined;

    this.addFieldDAO(itemDAO, field);
  }

  private formatSeries(data: LineSeriesDataPoint[], item: ComparisonItem, i: number): LineSeriesData {
    const DEFAULT_LINE_COLOR = '#2d8eff';

    return {
      data,
      color: item.color || DEFAULT_LINE_COLOR,
      name:
        (i === 0 ? formatLocalizedString(item.name, this.locale) : this.formatMessage('Comparison.IncomeStream.Scenario', { key: i })) +
        (this.proposal.showBaseCurrencies ? ` (${currencyToText(item.currency)})` : ''),
      dashStyle: i === 0 ? 'Solid' : 'Dash',
      lineWidth: i === 0 ? 2 : 1,
      tooltip: {
        valueDecimals: 2,
        valueSuffix: this.proposal.showBaseCurrencies ? ` ${item.currency}` : '',
      },
      itemType: item.type,
    };
  }
}
